package com.downdogapp.client.controllers

import com.downdogapp.*
import com.downdogapp.client.*
import com.downdogapp.client.Orientation.PORTRAIT
import com.downdogapp.client.api.*
import com.downdogapp.client.controllers.playback.PlaybackUtil
import com.downdogapp.client.controllers.playback.PlaybackViewController
import com.downdogapp.client.controllers.start.StartViewController
import com.downdogapp.client.singleton.*
import com.downdogapp.client.views.createLoadingView

class LoadingViewController(private val sequenceId: String?) : ViewController(PORTRAIT) {

  private var sequence: Sequence? = null
  private var playlist: Playlist? = null
  private var sequenceStartTime = Duration.ZERO
  private var videoOffsetTime = Duration.ZERO
  private var playbackUrl: String? = null
  private var player: MediaPlayer? = null
  private var currentProgress = 0.01

  private var madePracticeRequest = false
  private var backClicked = false
  private var movedToSequence = false
  private var startedTimer = false

  private val hasExited get() = movedToSequence || backClicked

  override val view = createLoadingView(
    controller = this,
    initialMessage = MANIFEST.loadingMessages.getOrNull(0) ?: "",
    initialProportion = currentProgress
  )

  init {
    MediaSampleManager.onStartingPractice()
    UserPrefs[Key.LoadingNumViews] = (UserPrefs[Key.LoadingNumViews] ?: 0) + 1
  }

  override fun onViewBecameVisible() {
    check(sequence == null) {
      "Already got sequenceResponse, not reposting request"
    }

    if (!startedTimer) {
      for (i in 1..<MANIFEST.loadingMessages.size) {
        val text = MANIFEST.loadingMessages[i]
        App.runAfter(MANIFEST.loadingMessageStartTimes[i]) {
          if (!hasExited) {
            view.setMessage(text)
          }
        }
      }

      updateLoadingProgress()

      if (sequenceId != null) {
        fetchPractice(
          sequenceId = sequenceId,
          sequenceToken = null
        )
      } else {
        waitForSequence { sequenceToken ->
          fetchPractice(
            sequenceId = null,
            sequenceToken = sequenceToken
          )
        }
      }
    }
  }

  private fun waitForSequence(isRetry: Boolean = false, callback: (String) -> Unit) {
    val desiredLength = SequenceSettings.selectedLengthOptionId
    SequenceSettings.useSequenceResponse {
      if (it == null) {
        logEvent("null_sequence")
        if (isRetry) {
          logEvent("sequence_retry_failed")
          App.alertNoInternet {
            onBackClicked()
            App.fetchManifest {}
          }
        } else {
          logEvent("sequence_retry")
          SequenceSettings.fetchSequences()
          waitForSequence(true, callback)
        }
      } else if (it.errorMessage != null) {
        logEvent("sequence_error", mapOf("message" to it.errorMessage))
        App.alert(message = it.errorMessage) {
          onBackClicked()
          App.fetchManifest {}
        }
      } else {
        val sequenceTokens = it.sequenceTokens
        val length = sequenceTokens.keys.find { it == desiredLength }
        val selectedSequence = sequenceTokens[length]
        if (selectedSequence == null) {
          logEvent("selected_length_invalid")
          App.alert(message = Strings.SEQUENCE_LENGTH_INVALID) {
            onBackClicked()
          }
        } else {
          callback(selectedSequence)
        }
      }
    }
  }

  private fun fetchPractice(sequenceId: String?, sequenceToken: String?) {
    Network.post(
      PracticeRequest(
        sequenceId = sequenceId,
        sequenceToken = sequenceToken,
        playlistSettings = SequenceSettings.playlistSettings,
        forOffline = false
      ),
      ::practiceRequestCallback
    )
    madePracticeRequest = true
  }

  private fun practiceRequestCallback(jsonResponse: String?) {
    if (!hasExited) {
      if (jsonResponse == null) {
        App.alertNoInternet { onBackClicked() }
        return
      }

      val practiceResponse = PracticeResponse.fromJson(jsonResponse)
      sequence = practiceResponse.sequence
      playlist = practiceResponse.playlist
      sequenceStartTime = practiceResponse.sequenceStartTime ?: Duration.ZERO

      App.pushWebHistory(url = "/play", hash = sequence!!.sequenceId)

      createBackgroundMediaPlayer(
        video = true,
        loop = false
      ) {
        player = it
        Network.post(
          PlaybackUtil.playbackUrlRequest(
            sequence = sequence!!,
            playlistId = playlist?.playlistId,
            videoOffsetTime = sequenceStartTime,
            airplay = player!!.isAirplayVideoActive
          )
        ) { json ->
          if (json == null) {
            logEvent(
              "playback_url_failed",
              mapOf("sequenceId" to sequence!!.sequenceId, "playlistId" to playlist?.playlistId)
            )
            App.alertNoInternet { onBackClicked() }
          } else {
            val response = PlaybackUrlResponse.fromJson(json)
            videoOffsetTime = response.videoOffsetTime
            playbackUrl = response.url
            logEvent(
              "playback_url_fetched",
              mapOf("sequenceId" to sequence!!.sequenceId, "playlistId" to playlist?.playlistId)
            )
            setPlayerFile()
          }
        }
      }

      logEvent("sequence_fetched", mapOf("sequenceId" to sequence!!.sequenceId))
    }
  }

  private fun setPlayerFile() {
    player!!.setFile(
      file = CommonUri(playbackUrl!!),
      seekTime = maxOf(Duration.ZERO, sequenceStartTime - videoOffsetTime)
    )
  }

  private fun updateLoadingProgress() {
    if (!hasExited) {
      if (playbackUrl != null && player?.canPlay == true) {
        view.setLoadedProportion(1.0)
        logEvent(
          "sequence_loaded",
          mapOf(
            "sequenceId" to sequence!!.sequenceId
          )
        )
        App.runAfter(animationDuration) {
          if (!backClicked) {
            movedToSequence = true
            App.pushViewController(
              PlaybackViewController(
                sequence = sequence!!,
                playlist = playlist,
                playbackUrl = playbackUrl!!,
                videoOffsetTime = videoOffsetTime,
                sequenceStartTime = sequenceStartTime,
                player = player!!
              )
            )
          }
        }
      } else {
        if (playbackUrl != null && player?.unexpectedIdleState == true) {
          // fallback to non-background player, this happens on Android if the notification is blocked
          logEvent("non_background_player_fallback")
          player!!.releasePlayer()
          player = createMediaPlayer(video = true, loop = false)
          setPlayerFile()
        } else {
          val target = when {
            !madePracticeRequest -> 0.2
            sequence == null -> 0.7
            playbackUrl == null -> 0.9
            else -> 0.95
          }
          currentProgress += (target - currentProgress) * 0.1
          view.setLoadedProportion(currentProgress)
        }

        App.runAfter(animationDuration) {
          updateLoadingProgress()
        }
      }
    }
  }

  override fun onBackClicked() {
    if (!hasExited) {
      backClicked = true
      logEvent("backed_out_of_loading_screen")
      playbackUrl?.let {
        Network.post(CancelStitchRequest(it))
      }
      App.unwindToViewController(StartViewController::class)
    }
  }

  override fun onClose() {
    playbackUrl?.let {
      Network.post(CancelStitchRequest(it))
    }
  }

  override fun onBackgrounded() {
    onBackClicked()
  }

  companion object {

    val animationDuration = seconds(0.5)
  }
}
